import Color from "color";
import React, { PropsWithChildren } from "react";
import { Pressable, Platform } from "react-native";
import { ColorType, useTheme } from "./AHTheme/AHTheme";
import { useBoxStyle } from "./AHTheme/useBoxStyle";
import { Box, TouchableBox } from "./AHTheme/Box";
import { AHIcon } from "./AHIcon";
import { AHText } from "./Text/AHText";

import { Circle } from "./AHTheme/Circle";
import { AHTeamIconCircleRelay } from "./AHTeamIconCircle";
import { graphql, useFragment } from "react-relay";
import { ListItemTeamIndicator_team$key } from "./__generated__/ListItemTeamIndicator_team.graphql";
import { Space, useLayoutContext } from "./LayoutProvider";

type DisplayMode = "LIST" | "TABLE";
export type ListItemProps = PropsWithChildren<{
  onPress?: () => void;
  onPressIn?: () => void;
  onPressOut?: () => void;
  isReorderable?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  isFree?: boolean;
  displayMode?: DisplayMode;
  disabled?: boolean;
  left?: React.ReactNode;
  leftEnabled?: boolean;
  footer?: React.ReactNode;
  right?: React.ReactNode;
  rightEnabled?: boolean;
  gap?: Space | "standard";
  isSelected?: boolean;
  selectedBackgroundColor?: string;
  forceHeight?: number;
  minHeight?: number;
  border?: ColorType;
  color?: ColorType;
}>;

const MINIMUM_LIST_ITEM_HEIGHT = 43;
const MINIMUM_TABLE_ITEM_HEIGHT = 50;

const ListItemCtx = React.createContext({
  isSelected: false,
});
export function ListItem({
  onPress,
  onPressIn,
  onPressOut,
  gap,
  children,
  isFirst,
  isLast,
  isFree,
  isReorderable,
  disabled,
  left,
  leftEnabled,
  right,
  rightEnabled,
  isSelected: selected,
  selectedBackgroundColor,
  forceHeight,
  minHeight,
  border,
  color,
  displayMode,
  footer,
}: ListItemProps) {
  const theme = useTheme();

  const _gap = gap === "standard" ? "small" : (gap ?? "none");

  const _displayMode = displayMode ?? "LIST";
  const _style = useBoxStyle({
    spaceHorizontal: "medium",
    cornerRadiusTopLeft: isFirst || isFree ? "large" : "none",
    cornerRadiusTopRight: isFirst || isFree ? "large" : "none",
    cornerRadiusBottomLeft: isLast || isFree ? "large" : "none",
    cornerRadiusBottomRight: isLast || isFree ? "large" : "none",
    borderColor: border,
  });
  const bgColor = color ? theme[color] : theme["surface-primary"];
  const selectedBG =
    selectedBackgroundColor ?? Color(bgColor).alpha(0.4).toString();
  const layout = useLayoutContext();

  const enforcedHeight =
    _displayMode === "LIST"
      ? MINIMUM_LIST_ITEM_HEIGHT
      : MINIMUM_TABLE_ITEM_HEIGHT;

  return (
    <Pressable
      disabled={disabled === true || !onPress}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={({ pressed }) => [
        _style,
        {
          borderWidth: border !== undefined ? 1 : 0,
          borderLeftWidth: border !== undefined ? 1 : 0,
          borderRightWidth: border !== undefined ? 1 : 0,
          borderBottomWidth: border !== undefined && (isLast || isFree) ? 1 : 0,
          borderTopWidth: border !== undefined && (isFirst || isFree) ? 1 : 0,
          height: forceHeight ?? (isReorderable ? enforcedHeight : undefined),
          minHeight: enforcedHeight,
          backgroundColor: isReorderable
            ? undefined
            : pressed
              ? Color(theme.primary).lighten(0.97).toString()
              : selected
                ? selectedBG
                : bgColor,
        },
      ]}
    >
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          minHeight: minHeight ?? enforcedHeight,
        }}
      >
        {left ? (
          <Box
            spaceRight="medium"
            style={{
              flexDirection: "row",
              alignItems: "center",
              opacity: leftEnabled === false ? 0 : 1,
            }}
          >
            {left}
          </Box>
        ) : null}
        <Box
          style={{
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            paddingRight: isReorderable ? layout.space.xlarge : undefined,
            height: forceHeight,
          }}
          gap={_gap}
          spaceVertical={
            forceHeight ? "none" : _displayMode === "LIST" ? "medium" : "small"
          }
        >
          <ListItemCtx.Provider
            value={{
              isSelected: selected === true,
            }}
          >
            {children}
          </ListItemCtx.Provider>
        </Box>
        {right ? (
          <Box
            spaceLeft="medium"
            style={{
              flexDirection: "row",
              alignItems: "center",
              maxWidth: "50%",
              justifyContent: "flex-end",
              opacity: rightEnabled === false ? 0 : 1,
            }}
          >
            {right}
          </Box>
        ) : null}
      </Box>
      {footer ? <Box spaceBottom="small">{footer}</Box> : null}
    </Pressable>
  );
}

function ListItemNavArrow() {
  return <AHIcon type="chevron-right" color="text-washed" size="medium" />;
}
function ListItemTitleC(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
  selectable?: boolean;
}) {
  const { isSelected } = React.useContext(ListItemCtx);
  return (
    <AHText
      type={isSelected ? "list-item-title-selected" : "list-item-title"}
      color={props.color}
      numberOfLines={props.numberOfLines}
      selectable={props.selectable ?? false}
    >
      {props.children}
    </AHText>
  );
}
const ListItemTitle = React.memo(ListItemTitleC);

function ListItemSecondaryTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  const { isSelected } = React.useContext(ListItemCtx);
  return (
    <AHText
      type={
        isSelected ? "list-item-title-selected" : "list-item-title-secondary"
      }
      selectable={false}
      color={props.color}
      numberOfLines={props.numberOfLines}
    >
      {props.children}
    </AHText>
  );
}
function ListItemAddTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  return (
    <AHText
      type="list-item-title-selected"
      color="primary"
      numberOfLines={props.numberOfLines}
    >
      {props.children}
    </AHText>
  );
}
function ListItemSubTitle(props: {
  children?: React.ReactNode;
  color?: ColorType;
  numberOfLines?: number;
}) {
  return (
    <AHText color={props.color} numberOfLines={props.numberOfLines}>
      {props.children}
    </AHText>
  );
}
function ListItemCheck() {
  return <AHIcon type="check" color="primary" />;
}

function ListItemDetailAction(props: { children?: React.ReactNode }) {
  return (
    <AHText selectable={false} spaceRight="xsmall" color="primary">
      {props.children}
    </AHText>
  );
}

function SelectIndicatorC(props: { selected: boolean; disabled?: boolean }) {
  return (
    <Circle
      size={Platform.OS === "web" ? "small" : "smallMedium"}
      color={props.selected ? "status-active" : "surface-backgroundControl"}
      borderColor={props.selected ? "status-active" : "border-strong"}
      style={{
        borderWidth: 1,
        opacity: props.disabled ? 0.5 : 1,
      }}
    />
  );
}

export const SelectIndicator = React.memo(SelectIndicatorC);
function SingleSelectIndicator(props: {
  selected: boolean;
  disabled?: boolean;
}) {
  return (
    <Circle
      size={Platform.OS === "web" ? "small" : "smallMedium"}
      color={props.selected ? "status-active" : "surface-backgroundControl"}
      borderColor={props.selected ? "status-active" : "border-strong"}
      style={{
        borderWidth: 1,
        opacity: props.disabled ? 0.5 : 1,
      }}
    />
  );
}
function MultiSelectIndicator(props: {
  selected: boolean | "indeterminate";
  disabled?: boolean;
  tintColor?: ColorType;
  customTintColor?: string;
}) {
  const tintColor = props.tintColor ?? "status-active";

  const theme = useTheme();
  const _tintColor = theme[tintColor];
  const selectedColor = props.customTintColor ?? _tintColor;

  const isFilled =
    props.selected === true || props.selected === "indeterminate";

  return (
    <Circle
      size={"smallSmallMedium"}
      style={{
        borderWidth: 1,

        opacity: props.disabled ? 0.5 : 1,
        borderColor: isFilled ? selectedColor : theme["border-strong"],
        backgroundColor: isFilled
          ? selectedColor
          : theme["surface-backgroundControl"],
      }}
    >
      {props.selected === "indeterminate" ? (
        <AHIcon type="minus" color="surface" size="xsmall" />
      ) : props.selected === true ? (
        <AHIcon type="check" color="surface" size="xsmall" />
      ) : null}
    </Circle>
  );
}

function RequiredSelectIndicator(props: {
  selected: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  onPress?: () => void;
}) {
  const theme = useTheme();

  const isFilled = props.selected === true;

  return (
    <TouchableBox
      style={{
        flexDirection: "row",
        alignItems: "center",
      }}
      gap="small"
      onPress={props.onPress}
    >
      {props.label ? (
        <AHText type="detail" color="darkText">
          {props.label}
        </AHText>
      ) : null}
      <Circle
        size={"smallSmallMedium"}
        style={{
          borderWidth: 1,
          opacity: props.disabled ? 0.5 : 1,
          borderColor: theme.darkerText,
          borderRadius: 5,
          backgroundColor: isFilled ? theme.darkerText : theme.surface,
        }}
      >
        <AHIcon type="check" color="surface" size="xsmall" />
      </Circle>
    </TouchableBox>
  );
}

function ListItemTeamIndicator(props: {
  team: ListItemTeamIndicator_team$key;
}) {
  const team = useFragment(
    graphql`
      fragment ListItemTeamIndicator_team on Team {
        ...AHTeamIconCircle
      }
    `,
    props.team
  );
  return <AHTeamIconCircleRelay size="smallMedium" team={team} />;
}

function ListItemRemoveButton(props: { onPress: () => void }) {
  return (
    <TouchableBox onPress={props.onPress}>
      <Circle color="negative" size="smallMedium">
        <AHIcon type="remove" color="surface" size="medium" />
      </Circle>
    </TouchableBox>
  );
}
function ListItemAddButton() {
  return <AHIcon type="add-circle" color="primary" size="large" />;
  // return (
  //   <Circle size="smallMedium" borderWidth={2} borderColor="primary">
  //     <AHIcon type="add" size="small" color="primary" />
  //   </Circle>
  // );
}

function ListItemEditableValueContainer(props: {
  editable: boolean;
  children: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <TouchableBox
      style={{
        flexDirection: "row",
        alignItems: "center",
        borderWidth: props.editable ? 1 : 0,
      }}
      pressRetentionOffset={{
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      }}
      hitSlop={{
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      }}
      space="small"
      spaceHorizontal={props.editable ? "small" : "none"}
      cornerRadius="medium"
      borderColor="border-primary"
      disabled={!props.editable}
      onPress={props.onPress}
    >
      {props.children}
    </TouchableBox>
  );
}
ListItem.AddButton = ListItemAddButton;
ListItem.RemoveButton = ListItemRemoveButton;
ListItem.Check = ListItemCheck;
ListItem.Title = ListItemTitle;
ListItem.TitleSecondary = ListItemSecondaryTitle;
ListItem.AddTitle = ListItemAddTitle;
ListItem.SubTitle = ListItemSubTitle;
ListItem.NavArrow = ListItemNavArrow;
ListItem.DetailActionText = ListItemDetailAction;
/**
 * @deprecated Use ListItem.SingleSelectIndicator or ListItem.MultiSelectIndicator instead
 */
ListItem.SelectIndicator = SelectIndicator;
ListItem.SingleSelectIndicator = SingleSelectIndicator;
ListItem.MultiSelectIndicator = MultiSelectIndicator;
ListItem.EditableValueContainer = ListItemEditableValueContainer;
ListItem.RequiredSelectIndicator = RequiredSelectIndicator;
ListItem.TeamIndicator = ListItemTeamIndicator;

ListItem.MINIMUM_LIST_ITEM_HEIGHT = MINIMUM_LIST_ITEM_HEIGHT;

/*

    EXAMPLES:
<ListItem right={<ListItem.NavArrow />}>
  <ListItem.Title>Lägg till konstrollpunkter</ListItem.Title>
</ListItem>;

<ListItem left={<AHTeamIconCircle type="cash" color="#33FF66" />} right={<ListItem.NavArrow />}>
  <ListItem.Title>11111111</ListItem.Title>
</ListItem>;

<ListItem
  left={<AHTeamIconCircle type="cash" color="#33FF66" />}
  right={<ListItem.NavArrow />}
>
  <ListItem.Title>11111111</ListItem.Title>
</ListItem>


<ListItem left={<Circle color="primary" />} right={<AHButton />}>
  <ListItem.Title>11111111</ListItem.Title>
  <ListItem.SubTitle>
    <AHIcon type="link" />
    Frukt, grönt{" "}
  </ListItem.SubTitle>
</ListItem>;

*/
